import React, { useEffect, useState, useMemo } from 'react';
import { View, ScrollView, Pressable, StyleSheet, Alert, ActivityIndicator, Switch } from 'react-native';
import { Text, TextInput } from '../ui/Text';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useAuth } from '../auth';
import {
  useRoom, useRooms, useIsAgent, useThreadRoots, loadThreadRoots, threadReplies, loadMembers, setHidden, leaveRoom, isHidden,
  addRoomToSpace, removeRoomFromSpace, setJoinRule, canSetState, canInvite, roomAdmin, sendText, senderName, setRoomName, MEvent,
  waitForEvent, isModelList, parseModelList, ModelOption, threadTitle, sameProvider, inviteByEmail, listEmailInvites, EmailInvite, isServiceUser, inviteToRoom, workspacesOf
} from '../matrix';
import { usePrefs } from '../prefs';
import { Btn } from '../ui/Btn';
import { KeyboardShift } from '../ui/KeyboardShift';
import { Avatar, RoomAvatar } from '../ui/Avatar';
import { RenameTitle } from '../ui/RenameTitle';
import { ActionSheet } from '../ui/ActionSheet';
import { confirmAction } from '../ui/confirm';
import { UserPicker } from '../ui/UserPicker';
import { radius, makeStyles, useTheme, webSwitch } from '../theme';
import type { RootStackParamList } from '../navTypes';

type Props = NativeStackScreenProps<RootStackParamList, 'RoomInfo'>;

export default function RoomInfoScreen({ route, navigation }: Props) {
  const theme = useTheme(); const s = useStyles();
  const { roomId } = route.params;
  const { userId } = useAuth();
  const room = useRoom(roomId);
  const all = useRooms();
  const isAgent = useIsAgent();
  const { humanBadge } = usePrefs();
  const threads = useThreadRoots(roomId);
  const spaces = all.filter((r) => r.isSpace);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [models, setModels] = useState<{ current?: string; currentProvider?: string; options: ModelOption[] } | null>(null);
  const [emailInvites, setEmailInvites] = useState<EmailInvite[]>([]);
  useEffect(() => { listEmailInvites(roomId).then(setEmailInvites).catch(() => {}); }, [roomId]);

  useEffect(() => { navigation.setOptions({ title: room?.name || 'Room' }); }, [room?.name]);
  useEffect(() => { if (room && !room.membersLoaded) loadMembers(roomId).catch(() => {}); }, [roomId, room?.membersLoaded]);
  useEffect(() => { loadThreadRoots(roomId).catch(() => {}); }, [roomId]);
  // Slack's guests: people in this room but not in its workspace. Telling needs the workspace's member list.
  const inWorkspaces = useMemo(() => workspacesOf(roomId, all), [all, roomId]);
  useEffect(() => { for (const sp of inWorkspaces) if (!sp.membersLoaded) loadMembers(sp.id).catch(() => {}); }, [inWorkspaces]);

  if (!room) return <View style={s.center}><ActivityIndicator color={theme.accent} /></View>;

  const run = async (key: string, fn: () => Promise<any>, ok?: string) => {
    setBusy(key); setNote(null);
    try { await fn(); if (ok) setNote(ok); }
    catch (e: any) { Alert.alert('That didn’t work', e?.message || 'Unknown error'); }
    finally { setBusy(null); }
  };

  const hidden = isHidden(room);
  const toggleHidden = () => run('hide', () => setHidden(roomId, !hidden), hidden ? 'Room unarchived' : 'Room archived');
  const leave = () => Alert.alert('Leave room?', `You'll stop receiving messages from "${room.name}".`, [
    { text: 'Cancel', style: 'cancel' },
    { text: 'Leave', style: 'destructive', onPress: () => run('leave', async () => { await leaveRoom(roomId); navigation.popToTop(); }) },
  ]);

  const adminId = roomAdmin(room);
  const adminName = adminId ? (adminId === userId ? 'you' : senderName(room, adminId)) : 'the room admin';
  const mayInvite = canInvite(room);
  // The invite bot is plumbing: it sits in the room so emailed people can be let in, and is not shown as a member.
  const members = Object.entries(room.members).filter(([u]) => !isServiceUser(u)).sort(([a, an], [b, bn]) => (a === userId ? -1 : b === userId ? 1 : an.localeCompare(bn)));
  const memberCount = members.length;
  const ai = room.agentInfo;
  const ctxPct = ai?.context_used != null && ai?.context_max ? Math.round((ai.context_used / ai.context_max) * 100) : null;
  const hasAgent = Object.keys(room.members).some(isAgent) || /fxm agent/i.test(room.nameState || room.name) || !!ai;
  const isGuest = (uid: string) => inWorkspaces.length > 0 && !isAgent(uid) && inWorkspaces.every((sp) => !sp.members[uid]);
  const guests = members.filter(([uid]) => isGuest(uid)).length;
  // Agent controls ride the same in-room command style Hermes already uses (!approve). Contract with Arjun.
  const cmd = (c: string, ok: string) => run(c, () => sendText(roomId, c), ok);
  // Nobody types commands: Helm sends `/model`, reads the agent's numbered list, shows it as a picker,
  // and sends `/model N` for the choice. (Hermes on Matrix does not act on the number reactions it suggests.)
  const pickModel = () => run(HERMES.model, async () => {
    const since = Date.now() - 2000;
    await sendText(roomId, HERMES.model);
    const reply = await waitForEvent(roomId, (e) => isAgent(e.sender) && isModelList(e), since, 25000);
    const parsed = reply ? parseModelList(reply.body) : null;
    if (!parsed) { setNote(reply ? 'The agent answered in the chat.' : 'No answer from the agent yet — its reply will show in the chat.'); return; }
    setModels(parsed);
  });
  // Hermes's /model takes the model NAME. A bare number is taken literally (parifma once ended up on a model called "8"),
  // and a name that exists on two providers picks the wrong one (parifma's 'default' jumped from its custom endpoint
  // to Mixture of Agents and lost its key). So: block that trap, confirm any provider change, and read the reply.
  const chooseModel = (o: ModelOption) => {
    const cur = models?.current, curProv = models?.currentProvider;
    const providerChanges = !!curProv && !!o.provider && !sameProvider(o.provider, curProv);
    if (o.id === cur && !providerChanges) { setNote(`${o.id} is already the current model.`); return; }
    if (o.id === cur && providerChanges) {
      Alert.alert('Same name, different provider', `${o.id} is what the agent runs now, but this entry is on ${o.provider}. Switching by name would move the agent off its ${curProv} provider and drop its endpoint and key. Not switching.`);
      return;
    }
    const send = () => run('switch', async () => {
      const since = Date.now() - 2000;
      await sendText(roomId, `${HERMES.model} ${o.id}`);
      const reply = await waitForEvent(roomId, (e) => isAgent(e.sender), since, 20000);
      if (!reply) { setNote(`Asked the agent to switch to ${o.id}; no answer yet, its reply will show in the chat.`); return; }
      if (/fail|error|not allowed|unauthori|invalid|cannot|unknown/i.test(reply.body)) {
        setNote(`After the switch the agent said: “${reply.body.replace(/\s+/g, ' ').slice(0, 140)}”. If it stays broken, restore it with helm-infra fleet/agent-set-model.sh.`);
      } else setNote(`Switched to ${o.id}${o.provider ? ` (${o.provider})` : ''}.`);
    });
    if (providerChanges) confirmAction('Switch provider too?', `${o.id} runs on ${o.provider}; the agent is on ${curProv} now. Its endpoint and key belong to ${curProv}, so the new provider must have its own credentials on the VM.`, 'Switch anyway', send);
    else confirmAction(`Switch to ${o.id}?`, o.provider ? `Provider: ${o.provider}` : 'Same provider as now.', 'Switch', send);
  };
  // Hermes built-in gateway commands (it also accepts the ! prefix). No custom Hermes work needed.
  const HERMES = {
    reset: '/new',      // fresh session id + history; earlier messages stay visible
    model: '/model',    // show (or switch) the model
    usage: '/usage',    // token usage, cost, context-window state
    context: '/context',// context-window gauge and breakdown
    status: '/status',  // session info + recap
  } as const;
  const openThread = (t: MEvent) => navigation.navigate('Thread', { roomId, rootId: t.id });

  return (
    <KeyboardShift>
    <ScrollView style={s.root} contentContainerStyle={{ padding: 16, paddingBottom: 40 }} keyboardShouldPersistTaps="handled">
      <View style={s.hero}>
        <RoomAvatar room={room} me={userId} isAgent={isAgent} size={56} humanBadge={humanBadge} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <RenameTitle name={room.name} canRename={canSetState(room, 'm.room.name') && !(room.isDm && memberCount <= 2)} onSave={(n) => setRoomName(roomId, n)} placeholder="Room name" />
          {!!room.topic && <Text style={s.topic}>{room.topic}</Text>}
          <Text style={s.meta}>{room.isDm ? 'Direct message' : 'Room'} · {memberCount} member{memberCount === 1 ? '' : 's'}{hidden ? ' · archived' : ''}</Text>
        </View>
      </View>
      {note ? <Text style={s.note}>{note}</Text> : null}
      <ActionSheet
        visible={!!models} title="Switch model" subtitle={models?.current ? `Now: ${models.current}${models.currentProvider ? ` on ${models.currentProvider}` : ''}` : 'Pick the model this agent should use'}
        actions={(models?.options || []).map((o) => {
          const differs = !!models?.currentProvider && !!o.provider && !sameProvider(o.provider, models.currentProvider);
          const isCurrent = models?.current === o.id && !differs; // the same name on another provider is not the current one
          return { label: `${o.id}${isCurrent ? '  · current' : ''}`, hint: `${o.provider || 'provider not listed'}${differs ? ' · different provider' : ''}`, onPress: () => chooseModel(o) };
        })}
        onClose={() => setModels(null)}
      />

      {hasAgent && (
        <View style={s.card}>
          <Text style={s.h}>Agent</Text>
          {ai ? (
            <>
              {!!ai.model && <Row k="Model" v={String(ai.model)} />}
              {ctxPct != null && <Row k="Context used" v={`${ctxPct}% (${fmtK(ai.context_used)} / ${fmtK(ai.context_max)})`} />}
              {ai.turns != null && <Row k="History window" v={`${ai.turns} turns`} />}
            </>
          ) : (
            <Text style={s.hint}>Model and memory usage will show here once the agent reports them. The buttons below ask the agent directly; it answers in the chat.</Text>
          )}
          {/* Each button sends a Hermes gateway command into the room; the agent answers in the room. */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
            <Btn label="Switch model" kind="ghost" onPress={pickModel} busy={busy === HERMES.model} />
            <Btn label="Context usage" kind="ghost" onPress={() => cmd(HERMES.context, 'Asked the agent for its context-window usage')} busy={busy === HERMES.context} />
            <Btn label="Usage & cost" kind="ghost" onPress={() => cmd(HERMES.usage, 'Asked the agent for token usage and cost')} busy={busy === HERMES.usage} />
            <Btn label="Status" kind="ghost" onPress={() => cmd(HERMES.status, 'Asked the agent for its status')} busy={busy === HERMES.status} />
            <Btn label="Reset conversation" kind="ghost" onPress={() => Alert.alert('Reset conversation?', 'The agent starts a fresh session in this room; the messages stay visible.', [
              { text: 'Cancel', style: 'cancel' }, { text: 'Reset', onPress: () => cmd(HERMES.reset, 'Asked the agent to start a fresh session') },
            ])} busy={busy === HERMES.reset} />
          </View>
        </View>
      )}

      <View style={s.card}>
        <View style={{ flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <Text style={s.h}>Threads</Text>
          {threads.length > 0 && <Pressable onPress={() => navigation.navigate('Threads', { roomId })} hitSlop={8}><Text style={{ color: theme.accent, fontSize: 13, fontWeight: '700' }}>All threads ›</Text></Pressable>}
        </View>
        {threads.length === 0 ? (
          <Text style={s.hint}>No threads yet. Long-press any message and choose “Reply in thread” to keep a side conversation out of the main room.</Text>
        ) : threads.slice(0, 8).map((t) => {
          const replies = threadReplies(roomId, t.id);
          const n = Math.max(t.threadCount || 0, replies.length);
          const latest = Math.max(t.threadLatestTs || 0, ...replies.map((r) => r.ts));
          return (
            <Pressable key={t.id} onPress={() => openThread(t)} style={({ pressed }) => [s.thread, pressed && { backgroundColor: theme.surface2 }]}>
              <Text style={s.threadBody} numberOfLines={2}>{threadTitle(t)}</Text>
              <Text style={s.threadMeta}>{t.sender === userId ? 'You' : senderName(room, t.sender)} · {n} {n === 1 ? 'reply' : 'replies'}{latest ? ` · ${fmtWhen(latest)}` : ''}</Text>
            </Pressable>
          );
        })}
      </View>

      <View style={s.card}>
        <Text style={s.h}>Invite someone</Text>
        {mayInvite ? (
          <>
            <UserPicker exclude={Object.keys(room.members)} confirmLabel="Invite" busy={busy === 'invite'}
              onPick={(u) => run('invite', async () => {
                const r = await inviteToRoom(roomId, u.id);
                setNote(`Invited ${u.name} (${r.mxid})${r.workspaces.length ? `, and into ${r.workspaces.map((w) => w.name).join(', ')} so they see the rest of it too` : ''}`);
              })}
              onPickEmail={(email) => run('invite', async () => {
                const r = await inviteByEmail(roomId, email);
                setNote(r.status === 'invited' ? `${email} already has an account and is now invited.` : `Invitation email sent to ${email}. They join once they sign up.`);
                setEmailInvites(await listEmailInvites(roomId));
              })} />
            {emailInvites.length > 0 && (
              <View style={{ marginTop: 10 }}>
                <Text style={s.hint}>Waiting to sign up</Text>
                {emailInvites.map((i) => <Text key={i.id} style={s.mId} numberOfLines={1}>✉ {i.email} · {new Date(i.at).toLocaleDateString([], { month: 'short', day: 'numeric' })}</Text>)}
              </View>
            )}
          </>
        ) : (
          <Text style={s.hint}>Only {adminName} can invite people to this room.</Text>
        )}
      </View>

      <View style={s.card}>
        <Text style={s.h}>Members</Text>
        {members.map(([uid, name]) => (
          <View key={uid} style={s.member}>
            <Avatar name={name} mxc={room.memberAvatars[uid]} kind={isAgent(uid) ? 'agent' : 'human'} size={32} humanBadge={humanBadge} me={uid === userId} />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={s.mName} numberOfLines={1}>{name}{uid === userId ? ' (you)' : ''}</Text>
              <Text style={s.mId} numberOfLines={1}>{uid}</Text>
            </View>
            {isAgent(uid) && <Text style={[s.tag, s.tagBot]}>Bot</Text>}
            {isGuest(uid) && <Text style={[s.tag, s.tagGuest]}>Guest</Text>}
            {uid === adminId && <Text style={s.tag}>Admin</Text>}
          </View>
        ))}
        {guests > 0 && <Text style={s.hint}>Guests are in this room but not in its workspace. They see only the rooms they were invited to.</Text>}
        {!room.membersLoaded && <ActivityIndicator color={theme.accent} style={{ marginTop: 8 }} />}
      </View>

      {!room.isSpace && (() => {
        const inSpaces = spaces.filter((sp) => sp.children.includes(roomId)).map((sp) => sp.id);
        const restricted = room.joinRule === 'restricted';
        const allowed = canSetState(room, 'm.room.join_rules');
        const canToggle = allowed && inSpaces.length > 0;
        const why = !inSpaces.length
          ? 'Add this room to a workspace first (below), then you can open it to workspace members.'
          : !allowed
            ? `You’re not an admin of this room${adminId && adminId !== userId ? ` — ${senderName(room, adminId)} is` : ''}, so this can’t be changed here. Rooms you create yourself (＋ New) are fully yours.`
            : restricted
              ? 'Anyone in the workspace can open this room on their own.'
              : 'Only people you invite can join — even if they’re in the workspace.';
        return (
          <View style={s.card}>
            <Text style={s.h}>Access</Text>
            <View style={s.switchRow}>
              <View style={{ flex: 1 }}>
                <Text style={s.switchLabel}>Workspace members can join</Text>
                <Text style={s.switchSub}>{restricted ? 'On — shared with the workspace' : 'Off — invite-only'}</Text>
              </View>
              {busy === 'access' ? <ActivityIndicator color={theme.accent} /> : (
                <Switch {...webSwitch}
                  value={restricted} disabled={!canToggle}
                  onValueChange={(v) => run('access', () => setJoinRule(roomId, v ? 'restricted' : 'invite', inSpaces), v ? 'Workspace members can now join' : 'Room is now invite-only')}
                  trackColor={{ true: theme.accent, false: theme.surface2 }} thumbColor="#fff"
                />
              )}
            </View>
            <Text style={s.hint}>{why}</Text>
          </View>
        );
      })()}

      {!room.isSpace && (() => {
        // Workspaces you manage get Add/Remove; ones you don't are listed only when this room is already in them.
        const rows = spaces.filter((sp) => canSetState(sp, 'm.space.child') || sp.children.includes(roomId));
        if (!rows.length) return null;
        return (
          <View style={s.card}>
            <Text style={s.h}>Workspaces</Text>
            {rows.map((sp) => {
              const inIt = sp.children.includes(roomId);
              const mine = canSetState(sp, 'm.space.child');
              const who = roomAdmin(sp);
              return (
                <View key={sp.id} style={s.member}>
                  <Avatar name={sp.name} mxc={sp.avatarMxc} kind="space" size={32} />
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={s.mName} numberOfLines={1}>{sp.name}</Text>
                    <Text style={s.mId} numberOfLines={1}>{mine ? (inIt ? 'In this workspace · you manage it' : 'Not in this workspace') : `In this workspace · managed by ${who ? senderName(sp, who) : 'someone else'}`}</Text>
                  </View>
                  {mine ? (
                    <Btn small label={inIt ? 'Remove' : 'Add'} kind={inIt ? 'ghost' : 'primary'} busy={busy === sp.id}
                      onPress={() => run(sp.id, () => (inIt ? removeRoomFromSpace(sp.id, roomId) : addRoomToSpace(sp.id, roomId)), inIt ? `Removed from ${sp.name}` : `Added to ${sp.name}`)} />
                  ) : null}
                </View>
              );
            })}
          </View>
        );
      })()}

      <View style={[s.card, { gap: 10 }]}>
        <Btn label={hidden ? 'Unarchive room' : 'Archive room'} kind="ghost" onPress={toggleHidden} busy={busy === 'hide'} />
        <Btn label="Leave room" kind="danger" onPress={leave} busy={busy === 'leave'} />
      </View>
      <Text style={s.idline} selectable>{room.id}</Text>
    </ScrollView>
    </KeyboardShift>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  const theme = useTheme(); const s = useStyles();
  return <View style={s.kv}><Text style={s.k}>{k}</Text><Text style={s.v} numberOfLines={1}>{v}</Text></View>;
}
function fmtK(n?: number) { return n == null ? '?' : n >= 1000 ? `${Math.round(n / 1000)}K` : String(n); }
function fmtWhen(ts: number) {
  const d = new Date(ts); const now = new Date();
  return d.toDateString() === now.toDateString() ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

const useStyles = makeStyles((theme) => StyleSheet.create({
  root: { flex: 1, backgroundColor: theme.bg },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: theme.bg },
  hero: { flexDirection: 'row', alignItems: 'center', gap: 14 },
  title: { color: theme.ink, fontSize: 22, fontWeight: '800' },
  topic: { color: theme.muted, fontSize: 14, marginTop: 4 },
  meta: { color: theme.faint, fontSize: 12, marginTop: 6, fontFamily: theme.mono },
  note: { color: theme.good, fontSize: 13, marginTop: 8, fontWeight: '600' },
  card: { backgroundColor: theme.surface, borderWidth: 1, borderColor: theme.border, borderRadius: radius.lg, padding: 14, marginTop: 14 },
  h: { color: theme.faint, fontSize: 11, fontWeight: '800', letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 10, fontFamily: theme.mono },
  hint: { color: theme.muted, fontSize: 13, marginBottom: 6, lineHeight: 18 },
  kv: { flexDirection: 'row', justifyContent: 'space-between', gap: 12, paddingVertical: 4 },
  k: { color: theme.muted, fontSize: 14 },
  v: { color: theme.ink, fontSize: 14, fontWeight: '600', flexShrink: 1, fontFamily: theme.mono },
  input: { backgroundColor: theme.surface2, borderWidth: 1, borderColor: theme.border, borderRadius: radius.md, color: theme.ink, fontSize: 15, paddingHorizontal: 12, paddingVertical: 10 },
  switchRow: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 4, marginBottom: 6 },
  switchLabel: { color: theme.ink, fontSize: 15, fontWeight: '700' },
  switchSub: { color: theme.faint, fontSize: 12, marginTop: 2 },
  member: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 7 },
  mName: { color: theme.ink, fontSize: 15, fontWeight: '600' },
  mId: { color: theme.faint, fontSize: 12, fontFamily: theme.mono },
  tag: { color: theme.muted, fontSize: 10.5, fontWeight: '800', letterSpacing: 0.6, textTransform: 'uppercase', backgroundColor: theme.surface2, borderRadius: radius.pill, paddingHorizontal: 8, paddingVertical: 3, overflow: 'hidden', fontFamily: theme.mono },
  tagBot: { color: theme.agentText, backgroundColor: theme.agentBg },
  tagGuest: { color: theme.warn },
  thread: { paddingVertical: 8, paddingHorizontal: 10, marginHorizontal: -6, borderRadius: radius.md, borderBottomWidth: 1, borderBottomColor: theme.border },
  threadBody: { color: theme.ink, fontSize: 14, lineHeight: 19 },
  threadMeta: { color: theme.accent, fontSize: 12, marginTop: 3, fontWeight: '700' },
  idline: { color: theme.faint, fontSize: 11, marginTop: 16, textAlign: 'center', fontFamily: theme.mono },
}));
